import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import { Activity, TrendingUp, Users, DollarSign, Eye, Calendar } from 'lucide-react';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import LiveBadge from '../../components/LiveBadge';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
  { key: 'custom', label: 'Custom' },
];

export default function ReferralStaffActivityPage() {
  const [period, setPeriod] = useState('week');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);           // staff id whose player list is shown
  const [playerList, setPlayerList] = useState(null);       // { staff, players }

  // Build the query string once per (period, startDate, endDate). Switching
  // to custom without valid dates keeps the previous data visible.
  const queryString = useMemo(() => {
    const p = new URLSearchParams({ period });
    if (period === 'custom') {
      if (startDate) p.set('start', startDate);
      if (endDate) p.set('end', endDate);
    }
    return p.toString();
  }, [period, startDate, endDate]);

  const fetchKpis = async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/admin/referral-staff/kpis?${queryString}`);
      setData(res.data);
    } catch { if (!silent) toast.error('Failed to load analytics'); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchKpis(); /* eslint-disable-next-line */ }, [queryString]);
  // Live auto-sync every 5s. Pauses while the player-drill-down modal is open
  // so admins can read without rows shifting under the cursor.
  useAutoRefresh(() => fetchKpis({ silent: true }), 5000, !playerList);

  const drillInto = async (staff) => {
    try {
      const res = await api.get(`/api/admin/referral-staff/${staff.id}/players?${queryString}`);
      setPlayerList(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const summary = data?.summary || {};
  const perStaff = data?.per_staff || [];

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="referral-staff-activity-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-3"><Activity className="w-5 h-5 text-[#D4AF37]" /> Referral Staff Activity <LiveBadge /></h2>
          <p className="text-sm text-gray-400">Live KPIs + per-staff breakdown of new sign-ups and first-deposit conversion.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
                period === p.key ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`ra-activity-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {period === 'custom' && (
        <div className="flex items-center gap-3 flex-wrap bg-[#131822] rounded-xl border border-white/10 p-3">
          <Calendar className="w-4 h-4 text-gray-500" />
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">From:</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-[#0B0E14] border-white/10 text-sm h-8 w-40" data-testid="ra-start-date" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">To:</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-[#0B0E14] border-white/10 text-sm h-8 w-40" data-testid="ra-end-date" />
          </div>
        </div>
      )}

      {/* Combined KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="ra-combined-kpis">
        <KPI label="Total Staff" value={summary.total_staff ?? 0} hint={`${summary.active_staff ?? 0} active`} color="#D4AF37" />
        <KPI label="New Players" value={summary.new_players ?? 0} icon={Users} color="#3B82F6" hint="in period" />
        <KPI label="Conv. Rate" value={`${summary.conversion_rate ?? 0}%`} icon={TrendingUp} color="#8b5cf6" hint={`${summary.players_deposited ?? 0} deposited`} />
        <KPI label="First Deposits" value={`$${Number(summary.first_deposit_total_sc ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} icon={DollarSign} color="#22c55e" />
        <KPI label="Total Deposits" value={`$${Number(summary.total_deposit_sc ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} color="#f59e0b" hint="lifetime of signups" />
        <KPI label="Avg 1st Deposit" value={`$${Number(summary.avg_first_deposit_sc ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} color="#ec4899" />
      </div>

      {/* Per-Staff breakdown table */}
      <div className="bg-[#131822] rounded-xl border border-white/10 overflow-x-auto">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Per-Staff Performance</h3>
          <span className="text-[10px] text-gray-500 uppercase tracking-wider">Click a row to drill into the players list</span>
        </div>
        <table className="w-full text-sm" data-testid="ra-per-staff-table">
          <thead><tr className="border-b border-white/5 text-xs text-gray-500">
            <th className="text-left p-3">Staff</th>
            <th className="text-left p-3">Code</th>
            <th className="text-right p-3">New Players</th>
            <th className="text-right p-3">Deposited</th>
            <th className="text-right p-3">Conv. Rate</th>
            <th className="text-right p-3">1st Deposit Total</th>
            <th className="text-right p-3">Avg 1st Deposit</th>
            <th className="text-right p-3">All Deposits</th>
            <th className="text-right p-3"></th>
          </tr></thead>
          <tbody>
            {loading && perStaff.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-gray-500">Loading…</td></tr>
            ) : perStaff.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-gray-500">No referral staff in scope yet.</td></tr>
            ) : perStaff.map(s => (
              <tr key={s.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer" onClick={() => drillInto(s)} data-testid={`ra-row-${s.id}`}>
                <td className="p-3">
                  <div className="font-medium">{s.full_name || s.username}</div>
                  <div className="text-xs text-gray-500">{s.email}</div>
                </td>
                <td className="p-3"><code className="text-[#D4AF37] text-xs">{s.referral_code}</code></td>
                <td className="p-3 text-right font-semibold text-blue-400">{s.kpis.new_players}</td>
                <td className="p-3 text-right text-green-400">{s.kpis.players_deposited}</td>
                <td className="p-3 text-right text-purple-400">{s.kpis.conversion_rate}%</td>
                <td className="p-3 text-right text-[#D4AF37] font-semibold">${Number(s.kpis.first_deposit_total_sc || 0).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                <td className="p-3 text-right text-gray-300">${Number(s.kpis.avg_first_deposit_sc || 0).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                <td className="p-3 text-right text-orange-400">${Number(s.kpis.total_deposit_sc || 0).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                <td className="p-3 text-right"><Eye className="w-4 h-4 text-gray-500 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drill-down modal */}
      <Modal open={!!playerList} onClose={() => setPlayerList(null)} title={`Players brought by ${playerList?.staff?.username || ''}`}>
        {playerList && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="ra-drilldown-players">
            {playerList.players.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No players in this period.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-500 border-b border-white/5">
                  <th className="text-left p-2">Player</th>
                  <th className="text-left p-2">Signed Up</th>
                  <th className="text-right p-2">1st Deposit</th>
                  <th className="text-right p-2">Lifetime Deposits</th>
                </tr></thead>
                <tbody>
                  {playerList.players.map(p => (
                    <tr key={p.user_id} className="border-b border-white/[0.03]">
                      <td className="p-2">
                        <div className="font-medium">{p.username}</div>
                        <div className="text-xs text-gray-500">{p.email}</div>
                      </td>
                      <td className="p-2 text-xs text-gray-400">{p.created_at ? new Date(p.created_at).toLocaleString() : '—'}</td>
                      <td className="p-2 text-right text-green-400">{p.first_deposit_sc > 0 ? `${Math.round(p.first_deposit_sc).toLocaleString()}` : <span className="text-gray-600">—</span>}</td>
                      <td className="p-2 text-right text-[#D4AF37]">{Math.round(p.total_deposit_sc).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function KPI({ label, value, hint, color = '#D4AF37', icon: Icon = null }) {
  return (
    <div className="bg-[#131822] rounded-xl border p-3" style={{ borderColor: `${color}33` }}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
      </div>
      <div className="text-xl font-bold mt-1 truncate" style={{ color }}>{value}</div>
      {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
}
